import type { LucideIcon } from 'lucide-react';
import {
  Gauge,
  GraduationCap,
  MessagesSquare,
  Sparkles,
  UsersRound,
  Wrench,
} from 'lucide-react';
import { activePath } from '@/lib/navMatch.ts';

export interface NavItem {
  /** The page's own address; detail pages under it match by prefix. */
  to: string;
  label: string;
  /**
   * Only for our own people. The Vue console hid these behind "superadmin with
   * an internal organisation"; the rule is unchanged.
   */
  internalOnly?: boolean;
  /** A count beside the name; hidden while it is zero. */
  badge?: 'open-urgent-reviews';
}

export interface NavGroupSection {
  /** Heading above this run of pages. Absent where the group reads as one list. */
  title?: string;
  items: NavItem[];
}

export interface NavGroup {
  id: string;
  /** The rail tile's name, and the name of the flyout that opens beside it. */
  label: string;
  icon: LucideIcon;
  /**
   * The group's pages, in the runs the flyout and the mobile panel draw them
   * in. Every group but Admin is a single unnamed run; Admin's sixteen tools
   * are a directory, and a directory needs its headings.
   */
  sections: NavGroupSection[];
}

/*
 * The one navigation definition. The rail, the flyout beside a tile and the
 * mobile panel all render from it, so they cannot drift apart in items, order,
 * or which one is current — hand-maintained copies of this list is exactly how
 * the Vue console's sidebar and its mobile menu diverged.
 *
 * Six tiles, not sixteen. The rail is a place to stand, not an index: a tile
 * per page made it taller than a laptop screen and squeezed every name into an
 * ellipsis. The page you want is one level in, in the popup that opens beside
 * its tile.
 */
export const NAV_GROUPS: NavGroup[] = [
  {
    id: 'coaching',
    label: 'Content',
    icon: GraduationCap,
    sections: [
      {
        items: [
          { to: '/tracks', label: 'Tracks' },
          { to: '/cohorts', label: 'Cohorts' },
          { to: '/topics', label: 'Topics' },
          { to: '/documents', label: 'Documents' },
          { to: '/meeting-templates', label: 'Meeting types', internalOnly: true },
          { to: '/journey-types', label: 'Journey types', internalOnly: true },
        ],
      },
    ],
  },
  {
    id: 'people',
    label: 'People',
    icon: UsersRound,
    sections: [
      {
        items: [
          { to: '/users', label: 'Users' },
          { to: '/journeys', label: 'Journeys', internalOnly: true },
          { to: '/organisations', label: 'Organisations', internalOnly: true },
        ],
      },
    ],
  },
  {
    id: 'conversations',
    label: 'Conversations',
    icon: MessagesSquare,
    sections: [
      {
        items: [
          { to: '/messages', label: 'Messages', internalOnly: true },
          {
            to: '/conversation-reviews',
            label: 'Reviews',
            internalOnly: true,
            badge: 'open-urgent-reviews',
          },
          { to: '/nps-responses', label: 'NPS', internalOnly: true },
        ],
      },
    ],
  },
  {
    id: 'ai',
    label: 'AI',
    icon: Sparkles,
    sections: [
      {
        items: [
          { to: '/skills', label: 'Skills', internalOnly: true },
          { to: '/insights', label: 'Insights', internalOnly: true },
        ],
      },
    ],
  },
  {
    id: 'dashboard',
    label: 'Dashboard',
    icon: Gauge,
    sections: [
      {
        items: [
          { to: '/dashboard', label: 'Dashboard' },
          /* /dashboard/questions is a page of the dashboard, not a destination of its own. */
          { to: '/dashboard/metrics-history', label: 'Metrics', internalOnly: true },
          { to: '/dashboard/page-usage', label: 'Page usage', internalOnly: true },
          { to: '/dashboard/costs', label: 'Costs', internalOnly: true },
        ],
      },
    ],
  },
  {
    id: 'admin',
    label: 'Admin',
    icon: Wrench,
    sections: [
      {
        title: 'Monitoring',
        items: [
          { to: '/admin/scheduled-tasks', label: 'Scheduled tasks', internalOnly: true },
          { to: '/admin/unanswered', label: 'Unanswered', internalOnly: true },
          { to: '/admin/pipeline', label: 'Pipeline', internalOnly: true },
          { to: '/admin/change-logs', label: 'Change logs', internalOnly: true },
          { to: '/admin/maintenance', label: 'Maintenance', internalOnly: true },
        ],
      },
      {
        title: 'Skills',
        items: [
          { to: '/admin/outreach-queue', label: 'Outreach', internalOnly: true },
          { to: '/admin/recommendations', label: 'Score preview', internalOnly: true },
          { to: '/admin/recommendation-runs', label: 'Run history', internalOnly: true },
          {
            to: '/admin/skill-broadcasts',
            label: 'Skill broadcasts',
            internalOnly: true,
          },
        ],
      },
      {
        title: 'Varia',
        items: [
          { to: '/admin/broadcast', label: 'Broadcast', internalOnly: true },
          { to: '/admin/bulk-import', label: 'Bulk import', internalOnly: true },
          { to: '/admin/notifications', label: 'Notifications', internalOnly: true },
          /*
           * Outside /admin and staying there — the URL is somebody's bookmark.
           * It sits here because it is the same kind of tool behind the same
           * inner door, which its own route gate applies for itself.
           */
          { to: '/demo', label: 'Demo', internalOnly: true },
        ],
      },
      {
        title: 'Integrations',
        items: [
          { to: '/admin/brevo', label: 'Brevo', internalOnly: true },
          { to: '/admin/mcp', label: 'MCP', internalOnly: true },
        ],
      },
      {
        title: 'Config',
        items: [
          { to: '/admin/feature-flags', label: 'Feature flags', internalOnly: true },
        ],
      },
    ],
  },
];

/** Every page in a group, headings dropped — the order they are drawn in. */
export function groupItems(group: NavGroup): NavItem[] {
  return group.sections.flatMap((section) => section.items);
}

/**
 * The groups this session is offered, with the pages it may not see removed. A
 * group whose every page is internal-only disappears with them: a tile leading
 * nowhere is worse than no tile.
 */
export function visibleGroups(internal: boolean): NavGroup[] {
  if (internal) return NAV_GROUPS;

  return NAV_GROUPS.map((group) => ({
    ...group,
    sections: group.sections
      .map((section) => ({
        ...section,
        items: section.items.filter((item) => !item.internalOnly),
      }))
      .filter((section) => section.items.length > 0),
  })).filter((group) => group.sections.length > 0);
}

/** Where a group's tile lands: its first page this session can actually open. */
export function groupHome(group: NavGroup): string {
  return groupItems(group)[0].to;
}

/**
 * Which group the current page belongs to. The page is found first and the
 * group read off it, so `/dashboard/metrics-history` cannot be claimed by a
 * shorter path in a different group.
 */
export function activeGroup(groups: NavGroup[], pathname: string): NavGroup | undefined {
  const current = activePath(
    pathname,
    groups.flatMap((group) => groupItems(group).map((item) => item.to)),
  );
  if (!current) return undefined;

  return groups.find((group) => groupItems(group).some((item) => item.to === current));
}
